const CIRCULACAO = {
  INTERNO: 'interno',
  EXTERNO: 'externo',
  TRANSICAO: 'transicao',
};

const normalizarCirculacao = (valor) => {
  const v = String(valor || '').toLowerCase().trim();
  if (v === CIRCULACAO.EXTERNO) return CIRCULACAO.EXTERNO;
  if (v === CIRCULACAO.TRANSICAO) return CIRCULACAO.TRANSICAO;
  return CIRCULACAO.INTERNO; // fallback padrão
};

const criarNodeMap = (nodes = []) => {
  const map = new Map();
  for (const n of nodes) map.set(n.id, n);
  return map;
};

const filtrarEdgesPorCirculacao = (edges = [], allowed = []) => {
  const allowedSet = new Set((allowed || []).map(normalizarCirculacao));
  if (!allowedSet.size) return edges;

  return edges.filter((e) => {
    const tipo = normalizarCirculacao(e?.circulacao);
    return allowedSet.has(tipo);
  });
};

/**
 * Decide o tipo de circulação do nó.
 * Prioridade:
 * 1) node.circulacao (se existir)
 * 2) função customizada passada nas options
 * 3) fallback null
 */
const getNodeCirculacaoDefault = (node, options = {}) => {
  if (!node) return null;

  if (node.circulacao) {
    return normalizarCirculacao(node.circulacao);
  }

  if (typeof options.getNodeCirculacao === 'function') {
    const v = options.getNodeCirculacao(node);
    return v ? normalizarCirculacao(v) : null;
  }

  return null;
};

export const findShortestPath = (nodes, edges, startNodeId, endNodeId, options = {}) => {
  if (!Array.isArray(nodes) || !Array.isArray(edges)) return null;
  if (!startNodeId || !endNodeId) return null;

  const distances = {};
  const prev = {};
  const pq = new Set(nodes.map(n => n.id));

  // Otimização: evita vários nodes.find(...) dentro do loop
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  // Penalidade configurável para "destinos intermediários bloqueados"
  const destinationBlockPenalty = Number(options?.destinationBlockPenalty ?? 10000);

  // Callback opcional vindo da findShortestPathWithContext / frontend
  const getNodePermiteCirculacao = (node) => {
    if (!node) return false;

    // 1) Se já vier no próprio nó (boolean)
    if (typeof node.permite_circulacao === 'boolean') {
      return node.permite_circulacao;
    }

    // 2) Se vier como string/num
    if (node.permite_circulacao !== undefined && node.permite_circulacao !== null) {
      const raw = String(node.permite_circulacao).trim().toLowerCase();
      if (raw === '1' || raw === 'true' || raw === 'sim') return true;
      if (raw === '0' || raw === 'false' || raw === 'nao' || raw === 'não') return false;
    }

    // 3) Se foi injetado por callback
    if (typeof options?.getNodePermiteCirculacao === 'function') {
      try {
        return !!options.getNodePermiteCirculacao(node);
      } catch (e) {
        return false;
      }
    }

    // 4) fallback
    return false;
  };

  nodes.forEach(node => {
    distances[node.id] = Infinity;
    prev[node.id] = null;
  });

  distances[startNodeId] = 0;

  while (pq.size > 0) {
    const pqArr = [...pq];
    let closestNodeId = pqArr.reduce(
      (min, id) => (distances[id] < distances[min] ? id : min),
      pqArr[0]
    );

    if (distances[closestNodeId] === Infinity) break;

    pq.delete(closestNodeId);

    if (closestNodeId === endNodeId) break;

    const neighbors = edges.filter(
      edge => edge.source === closestNodeId || edge.target === closestNodeId
    );

    for (const edge of neighbors) {
      const neighborId = edge.source === closestNodeId ? edge.target : edge.source;
      if (!pq.has(neighborId)) continue;

      const currentNode = nodeMap.get(closestNodeId);
      // const neighborNode = nodeMap.get(neighborId); // se precisar usar depois

      let weightAdjustment = Number(edge.weight || 0);

      /**
       * Regra de bloqueio:
       * - se o nó atual é um "destination" intermediário
       * - e NÃO é o destino final da rota
       * - e NÃO permite circulação
       * => aplica penalidade alta (desestimula passar por dentro de estandes)
       *
       * Isso libera Entrada/Saída/Portão (ou outros tipos) quando
       * permite_circulacao = true
       */
      if (currentNode?.type === 'destination' && closestNodeId !== startNodeId) {
        const destinoEhFinal = currentNode.id === endNodeId;
        const permiteCirculacao = getNodePermiteCirculacao(currentNode);

        if (!destinoEhFinal && !permiteCirculacao) {
          weightAdjustment += destinationBlockPenalty;
        }
      }

      const newDist = distances[closestNodeId] + weightAdjustment;

      if (newDist < distances[neighborId]) {
        distances[neighborId] = newDist;
        prev[neighborId] = closestNodeId;
      }
    }
  }

  const pathIds = [];
  let currentNodeId = endNodeId;

  if (prev[currentNodeId] === null && currentNodeId !== startNodeId) return null;

  while (currentNodeId) {
    pathIds.unshift(currentNodeId);
    currentNodeId = prev[currentNodeId];
  }

  if (pathIds[0] === startNodeId) {
    const pathNodes = pathIds.map(id => nodeMap.get(id)).filter(Boolean);

    return {
      path: pathNodes,
      distance: distances[endNodeId],
    };
  }

  return null;
};

/**
 * Nova função de rota com contexto de circulação.
 *
 * options:
 * - mode: 'auto' | 'flexivel' | 'interno_only' | 'externo_only'
 * - getNodeCirculacao: (node) => 'interno'|'externo'|'transicao'|null
 * - allowFallback: boolean (default true)
 * - debug: boolean
 *
 * Retorno:
 * {
 *   path,
 *   distance,
 *   meta: {
 *     strategy,
 *     startCirculacao,
 *     endCirculacao,
 *     attempted
 *   }
 * }
 */
export const findShortestPathWithContext = (
  nodes,
  edges,
  startNodeId,
  endNodeId,
  options = {}
) => {
  const {
    mode = 'auto',
    allowFallback = true,
    debug = false,
    getNodePermiteCirculacao,     // NOVO
    destinationBlockPenalty = 10000, // NOVO (opcional)
  } = options;

  if (!Array.isArray(nodes) || !Array.isArray(edges)) return null;
  if (!startNodeId || !endNodeId) return null;

  const nodeMap = criarNodeMap(nodes);
  const startNode = nodeMap.get(startNodeId);
  const endNode = nodeMap.get(endNodeId);

  if (!startNode || !endNode) return null;

  const startCirculacao = getNodeCirculacaoDefault(startNode, options);
  const endCirculacao = getNodeCirculacaoDefault(endNode, options);

  const ambosInternos =
    startCirculacao === CIRCULACAO.INTERNO &&
    endCirculacao === CIRCULACAO.INTERNO;

  const ambosExternos =
    startCirculacao === CIRCULACAO.EXTERNO &&
    endCirculacao === CIRCULACAO.EXTERNO;

  const tried = [];

  // helper para executar uma tentativa com filtro de circulação
  const tentar = (strategy, allowedCirculacoes = null) => {
    const edgesUsadas = allowedCirculacoes
      ? filtrarEdgesPorCirculacao(edges, allowedCirculacoes)
      : edges;

    tried.push({
      strategy,
      allowedCirculacoes: allowedCirculacoes ? [...allowedCirculacoes] : 'all',
      edgesCount: edgesUsadas.length,
    });

    // const result = findShortestPath(nodes, edgesUsadas, startNodeId, endNodeId);
    const result = findShortestPath(nodes, edgesUsadas, startNodeId, endNodeId, options);
    if (!result) return null;

    return {
      ...result,
      meta: {
        strategy,
        startCirculacao,
        endCirculacao,
        attempted: tried,
      },
    };
  };

  // ===== modos explícitos =====
  if (mode === 'interno_only') {
    return tentar('interno_only', [CIRCULACAO.INTERNO, CIRCULACAO.TRANSICAO]);
  }

  if (mode === 'externo_only') {
    return tentar('externo_only', [CIRCULACAO.EXTERNO, CIRCULACAO.TRANSICAO]);
  }

  if (mode === 'flexivel') {
    return tentar('flexivel', null);
  }

  // ===== modo automático =====
  // 1) Se ambos internos -> tenta interno/transição primeiro
  if (ambosInternos) {
    const preferida = tentar('auto_internal_preferred', [
      CIRCULACAO.INTERNO,
      CIRCULACAO.TRANSICAO,
    ]);
    if (preferida) return preferida;

    if (allowFallback) {
      const fallback = tentar('auto_fallback_flexivel', null);
      if (fallback) return fallback;
    }

    if (debug) {
      console.warn('[Rota] Falhou no modo auto (ambos internos).', { tried });
    }
    return null;
  }

  // 2) Se ambos externos -> opcionalmente tenta externo/transição primeiro
  if (ambosExternos) {
    const preferida = tentar('auto_external_preferred', [
      CIRCULACAO.EXTERNO,
      CIRCULACAO.TRANSICAO,
    ]);
    if (preferida) return preferida;

    if (allowFallback) {
      const fallback = tentar('auto_fallback_flexivel', null);
      if (fallback) return fallback;
    }

    if (debug) {
      console.warn('[Rota] Falhou no modo auto (ambos externos).', { tried });
    }
    return null;
  }

  // 3) Casos mistos (interno->externo, externo->interno, desconhecido)
  // Vai no flexível direto
  const flex = tentar('auto_mixed_flexivel', null);
  if (flex) return flex;

  if (debug) {
    console.warn('[Rota] Falhou no modo auto (caso misto).', { tried });
  }
  return null;
};

export const generateDirections = (pathNodes) => {
  if (!pathNodes || pathNodes.length < 2) return ["Rota inválida."];

  const normName = (s) => String(s || "").trim().toLowerCase();

  // distância total (pra decidir "curto/médio/longo")
  const dist = (a, b) => Math.hypot((b.x - a.x), (b.y - a.y));
  const totalDist = pathNodes.reduce((acc, n, idx) => {
    if (idx === 0) return 0;
    return acc + dist(pathNodes[idx - 1], n);
  }, 0);

  const directions = [`Saia de <strong>${pathNodes[0].name}</strong>.`];

  // Buffer do "trecho longo" (mesma rua + em linha)
  let straightBuffer = null;
  const flushStraight = () => {
    if (!straightBuffer) return;

    const { streetName, d, crossings } = straightBuffer;

    // heurística pra ficar "humano"
    const ratio = totalDist > 0 ? (d / totalDist) : 0;
    let trecho = "por um trecho";
    if (ratio >= 0.60 || d >= 600) trecho = "por um longo trecho";
    else if (ratio >= 0.30 || d >= 300) trecho = "por um bom trecho";
    else if (ratio >= 0.15 || d >= 150) trecho = "por um trecho médio";
    else trecho = "por um trecho curto";

    let cruzamentosTxt = "";
    if (crossings >= 6) cruzamentosTxt = `, passando por <strong>vários cruzamentos</strong>`;
    else if (crossings >= 3) cruzamentosTxt = `, passando por <strong>alguns cruzamentos</strong>`;
    else if (crossings >= 1) cruzamentosTxt = `, passando pelo <strong>próximo cruzamento</strong>`;

    directions.push(`Siga pela <strong>${streetName}</strong> ${trecho}${cruzamentosTxt}.`);
    straightBuffer = null;
  };

  const getTurn = (prevNode, currentNode, nextNode) => {
    // Inverte Y porque no mapa/tela o Y cresce pra baixo
    const a1x = currentNode.x - prevNode.x;
    const a1y = -(currentNode.y - prevNode.y);

    const a2x = nextNode.x - currentNode.x;
    const a2y = -(nextNode.y - currentNode.y);

    const angle1 = Math.atan2(a1y, a1x);
    const angle2 = Math.atan2(a2y, a2x);

    let angleDiff = ((angle2 - angle1) * 180) / Math.PI;

    if (angleDiff > 180) angleDiff -= 360;
    if (angleDiff < -180) angleDiff += 360;

    if (angleDiff > 45 && angleDiff < 135) return "left";
    if (angleDiff < -45 && angleDiff > -135) return "right";
    if (Math.abs(angleDiff) <= 45) return "straight";
    return "uturn";
  };

  // 1) primeiro movimento: em vez de "Siga em direção a Rua 01", fica mais natural:
  // "Acesse a Rua 01."
  directions.push(`Acesse a <strong>${pathNodes[1].name}</strong>.`);

  for (let i = 2; i < pathNodes.length; i++) {
    const prev = pathNodes[i - 2];
    const current = pathNodes[i - 1];
    const next = pathNodes[i];
    const isFinal = i === pathNodes.length - 1;

    const turn = getTurn(prev, current, next);

    // Se for final, antes de falar do destino, fecha qualquer trecho longo em buffer
    if (isFinal) {
      flushStraight();
      directions.push(`Siga até o seu destino: <strong>${next.name}</strong>.`);
      break;
    }

    // Regra de ouro: se estamos andando "reto" e permanecendo na mesma rua, agrupa.
    const sameStreet = normName(current.name) === normName(next.name);

    if (turn === "straight" && sameStreet) {
      // cria/atualiza buffer
      if (!straightBuffer) {
        straightBuffer = {
          streetName: current.name,
          d: 0,
          crossings: 0,
        };
      }
      straightBuffer.d += dist(current, next);
      straightBuffer.crossings += 1;
      continue;
    }

    // Se saiu do padrão (não é reto ou trocou de rua), fecha o trecho longo antes
    flushStraight();

    // Agora escreve a ação mais humana:
    if (turn === "right" || turn === "left") {
      const dirTxt = (turn === "right") ? "à direita" : "à esquerda";

      // Se o nome não muda, é mais “curva” do que “vire” (fica bem mais natural)
      if (sameStreet) {
        directions.push(`Faça uma curva ${dirTxt} e continue pela <strong>${current.name}</strong>.`);
      } else {
        directions.push(`Vire ${dirTxt} para seguir em direção a <strong>${next.name}</strong>.`);
      }
      continue;
    }

    if (turn === "uturn") {
      directions.push(`Faça o retorno e siga em direção a <strong>${next.name}</strong>.`);
      continue;
    }

    // straight mas trocou de nome (ou caso raro)
    directions.push(`Siga em frente em direção a <strong>${next.name}</strong>.`);
  }

  directions.push(`🎉 Você chegou ao seu destino: <strong>${pathNodes[pathNodes.length - 1].name}</strong>!`);
  return directions;
};
